/**
 * Rate Limit Activity (Admin)
 *
 * Answers: "did anyone get rate-limited recently?"
 *
 * Lists hits from biz_rate_limit_events grouped per limiter, with filters
 * for time window, limiter type, key type (user/email/ip), and a free-text
 * search across email/IP/key value. Admin-only via the API route guard.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDateTime } from '@/lib/utils';
import {
  Shield,
  Search,
  RefreshCw,
  AlertTriangle,
  User,
  Mail,
  Globe,
  Loader2,
} from 'lucide-react';

interface RateLimitEvent {
  id: string;
  eventTime: string;
  limiterName: string;
  keyValue: string;
  keyType: 'user' | 'email' | 'ip';
  userId: string | null;
  userEmail: string | null;
  userFullName: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  endpoint: string;
  method: string;
}

interface RateLimitSummaryRow {
  limiterName: string;
  eventsPastDay: number;
  eventsPastWeek: number;
  distinctKeysPastWeek: number;
}

const LIMITER_LABELS: Record<string, string> = {
  global: 'Global API',
  login: 'Login',
  'password-change': 'Password Change',
  'expense-import': 'Expense Import',
  widget: 'Helpdesk Widget',
};

const SINCE_OPTIONS = [
  { value: '1h', label: 'Past hour', hours: 1 },
  { value: '24h', label: 'Past 24 hours', hours: 24 },
  { value: '7d', label: 'Past 7 days', hours: 24 * 7 },
  { value: '30d', label: 'Past 30 days', hours: 24 * 30 },
];

function sincePresetToIso(preset: string): string {
  const opt = SINCE_OPTIONS.find(o => o.value === preset) || SINCE_OPTIONS[2];
  return new Date(Date.now() - opt.hours * 60 * 60 * 1000).toISOString();
}

function keyTypeIcon(t: string) {
  if (t === 'user') return <User className="h-3.5 w-3.5" />;
  if (t === 'email') return <Mail className="h-3.5 w-3.5" />;
  return <Globe className="h-3.5 w-3.5" />;
}

export function RateLimitActivityPage() {
  const [search, setSearch] = useState('');
  const [limiterFilter, setLimiterFilter] = useState('');
  const [keyTypeFilter, setKeyTypeFilter] = useState('');
  const [sincePreset, setSincePreset] = useState('7d');

  const { data: summaryData, refetch: refetchSummary, isFetching: summaryFetching } = useQuery({
    queryKey: ['rateLimitSummary'],
    queryFn: () => api.getRateLimitSummary(),
  });

  const { data: eventsData, refetch: refetchEvents, isFetching: eventsFetching } = useQuery({
    queryKey: ['rateLimitEvents', { search, limiterFilter, keyTypeFilter, sincePreset }],
    queryFn: () => api.getRateLimitEvents({
      limit: 200,
      limiterName: limiterFilter || undefined,
      keyType: keyTypeFilter || undefined,
      search: search || undefined,
      since: sincePresetToIso(sincePreset),
    }),
  });

  const summary = (summaryData?.data || []) as RateLimitSummaryRow[];
  const events = (eventsData?.data || []) as RateLimitEvent[];

  function refreshAll() {
    refetchSummary();
    refetchEvents();
  }

  const totalPastDay = summary.reduce((s, r) => s + r.eventsPastDay, 0);
  const totalPastWeek = summary.reduce((s, r) => s + r.eventsPastWeek, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Rate Limit Activity
          </h1>
          <p className="text-muted-foreground">
            Audit log of rate-limit hits — who got throttled, when, and where.
          </p>
        </div>
        <Button variant="outline" onClick={refreshAll} disabled={summaryFetching || eventsFetching}>
          <RefreshCw className={`h-4 w-4 mr-2 ${summaryFetching || eventsFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Past 24 hours</p>
                <p className="text-2xl font-bold">{totalPastDay}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <Shield className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Past 7 days</p>
                <p className="text-2xl font-bold">{totalPastWeek}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-2">Note</div>
            <p className="text-xs text-muted-foreground">
              Hits are deduped: one row per limiter+key per 5-min window.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Per-limiter breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">By Limiter</CardTitle>
        </CardHeader>
        <CardContent>
          {summary.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No rate-limit hits in the past week.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 font-medium">Limiter</th>
                  <th className="text-right p-2 font-medium">Past 24h</th>
                  <th className="text-right p-2 font-medium">Past 7 days</th>
                  <th className="text-right p-2 font-medium">Distinct keys (7d)</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((r) => (
                  <tr key={r.limiterName} className="border-b last:border-b-0">
                    <td className="p-2 font-medium">{LIMITER_LABELS[r.limiterName] || r.limiterName}</td>
                    <td className="p-2 text-right">{r.eventsPastDay}</td>
                    <td className="p-2 text-right">{r.eventsPastWeek}</td>
                    <td className="p-2 text-right text-muted-foreground">{r.distinctKeysPastWeek}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search email, IP, key..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={limiterFilter} onValueChange={(v) => setLimiterFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All limiters" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All limiters</SelectItem>
            <SelectItem value="global">Global API</SelectItem>
            <SelectItem value="login">Login</SelectItem>
            <SelectItem value="password-change">Password Change</SelectItem>
            <SelectItem value="expense-import">Expense Import</SelectItem>
            <SelectItem value="widget">Helpdesk Widget</SelectItem>
          </SelectContent>
        </Select>
        <Select value={keyTypeFilter} onValueChange={(v) => setKeyTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="All keys" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All keys</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="ip">IP</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sincePreset} onValueChange={setSincePreset}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SINCE_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Events table */}
      <Card>
        <CardContent className="p-0">
          {eventsFetching && events.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Shield className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No rate-limit events match these filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">When</th>
                    <th className="text-left p-3 font-medium">Limiter</th>
                    <th className="text-left p-3 font-medium">Key</th>
                    <th className="text-left p-3 font-medium">User / IP</th>
                    <th className="text-left p-3 font-medium">Endpoint</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-b hover:bg-muted/30">
                      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(e.eventTime)}
                      </td>
                      <td className="p-3">
                        <Badge variant="warning" className="text-xs">
                          {LIMITER_LABELS[e.limiterName] || e.limiterName}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5">
                          {keyTypeIcon(e.keyType)}
                          <span className="font-mono text-xs">{e.keyValue}</span>
                        </div>
                      </td>
                      <td className="p-3 text-xs">
                        {e.userEmail ? (
                          <div>
                            <div className="font-medium">{e.userFullName || e.userEmail}</div>
                            {e.userFullName && <div className="text-muted-foreground">{e.userEmail}</div>}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">{e.ipAddress || '—'}</span>
                        )}
                      </td>
                      <td className="p-3 text-xs">
                        <span className="font-mono text-muted-foreground">
                          {e.method} {e.endpoint}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
